import axios from 'axios'
import _ from 'lodash'
import { DateTime } from 'luxon'
import localForage from 'localforage'

const StorageKey = 'shoppingList_v1'

/*
  API Routes:
    /api
      /categories
        /                   GET       - get all categories
        /                   PUT       - add category (name)
        /:id                GET       - get single category
        /:id                POST      - update category (rename)
        /:id                DELETE    - archive category, error if items still assigned
        /:id/restore        POST      - restore archived category

      /items
        /                   GET       - get all items (and store links)
        /                   PUT       - add item
        /:id                GET       - get single item (and store links)
        /:id                POST      - update item
        /:id                DELETE    - archive item (and meal ingredients, store links, purchase history)
        /:id/restore        POST      - restore archived item (and meal ingredients, store links, purchase history)
        /:id/quantity/:qty  POST      - set item quantity needed (:qty int)
        /:id/cart/:status   POST      - set item in/out of cart (:status 'in')
        /actions/purchase   POST      - mark all items in cart as purchased, remove from cart

      /meals
        /                   GET       - get all meals
        /                   PUT       - add meal (name)
        /:id                GET       - get single meal (and all ingredients)
        /:id                POST      - update mean (rename)
        /:id                DELETE    - archive meal (and all ingredients)
        /:id/restore        POST      - restore archived meal (and all ingredients)

        /:meal_id/ingredients
          /                 GET       - get all ingredients for this meal
          /                 PUT       - add ingredient to this meal
          /:id              POST      - update ingredient in this meal
          /:id              DELETE    - archive ingredient from this meal
          /:id/restore      POST      - restore archived ingredient to this meal

      /meta
        /lastUpdate         GET       - get last update time/who stamps for entities (query ?type=item&type=meal, etc)

      /stores
        /                   GET       - get all stores
        /                   PUT       - create store (name)
        /:id                GET       - get store (and all item links)
        /:id                POST      - update store (rename)
        /:id                DELETE    - archive store (and all item links)
        /:id/restore        POST      - restore archived store (and all item links)

        /:store_id/items
          /                 GET       - get all item links for this store
          /                 PUT       - link item to this store
          /:id              DELETE    - delete item link by ID from this store (PERMANENT)
 */

const internals = {
  // region - Helpers
  parseDate (date) {
    return date ? DateTime.fromFormat(date, 'yyyy-MM-dd') : null
  },
  processAuditedFields (model) {
    return {
      createdAt: DateTime.fromISO(model.createdAt),
      updatedAt: DateTime.fromISO(model.updatedAt),
      deletedAt: model.deletedAt ? DateTime.fromISO(model.deletedAt) : null,
      isDeleted: !!model.deletedAt
    }
  },
  processStoreCategory (sc) {
    return _.assign({}, sc, internals.processAuditedFields(sc))
  },
  processStoreItem (si) {
    return _.assign({}, si, internals.processAuditedFields(si))
  },
  processCategory (c) {
    return _.assign({}, c, internals.processAuditedFields(c), {
      storeCategories: _.map(c.storeCategories, internals.processStoreCategory)
    })
  },
  processStore (s) {
    return _.assign({}, s, internals.processAuditedFields(s), {
      storeCategories: _.map(s.storeCategories, internals.processStoreCategory),
      storeItems: _.map(s.storeItems, internals.processStoreItem)
    })
  },
  processMealIngredient (mi) {
    return _.assign({}, mi, internals.processAuditedFields(mi))
  },
  processMeal (m) {
    return _.assign({}, m, internals.processAuditedFields(m), {
      ingredients: _.map(m.ingredients, internals.processMealIngredient)
    })
  },
  processItem (item) {
    return _.assign({}, item, internals.processAuditedFields(item), {
      lastPurchasedOn: internals.parseDate(item.lastPurchasedOn),
      nextPurchaseEstimate: internals.parseDate(item.nextPurchaseEstimate),
      hasNoPrice: !item.priceEachCents,
      totalPriceCents: item.priceEachCents * item.quantityNeeded,
      storeIds: item.storeIds || []
    })
  },
  // endregion
  // region - Auth
  setAuthKeyHeader (key) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${key}`
  },
  async hasValidAuthKey () {
    const key = await localForage.getItem(`${StorageKey}.authKey`)
    if (!key) {
      return false
    }
    // Validate the key
    try {
      internals.setAuthKeyHeader(key)
      await axios.get('/auth/check')
      return true
    } catch (e) {
      console.log('Bad auth key', e)
      internals.clearAuthKey()
      return false
    }
  },
  async setAuthKey (key) {
    try {
      await localForage.setItem(`${StorageKey}.authKey`, key)
      internals.setAuthKeyHeader(key)
      return true
    } catch (e) {
      console.error(`Error setting auth key: `, e)
      return false
    }
  },
  async clearAuthKey () {
    try {
      await localForage.removeItem(`${StorageKey}.authKey`)
      internals.setAuthKeyHeader('')
      return true
    } catch (e) {
      console.error(`Error clearing auth key: `, e)
      return false
    }
  },
  // endregion
  // region - Items
  async getAllItems () {
    const resp = await axios.get(`/api/items`)
    return _.map(resp.data, internals.processItem)
  },
  async getNeededItems () {
    const resp = await axios.get(`/api/items?needed=y`)
    return _.map(resp.data, internals.processItem)
  },
  async getItemForId ({ id }) {
    const resp = await axios.get(`/api/items/${id}`)
    return internals.processItem(resp.data)
  },
  async saveItem (item) {
    const resp = await (item.id
      ? axios.post(`/api/items/${item.id}`, item)
      : axios.put(`/api/items`, item))
    return internals.processItem(resp.data)
  },
  async deleteItemForId ({ id }) {
    const resp = await axios.delete(`/items/${id}`)
    return resp.data
  },
  async restoreItemForId ({ id }) {
    const resp = await axios.post(`/api/items/${id}/restore`)
    return resp.data
  },
  async setItemQuantityForId ({ id, quantityNeeded }) {
    const resp = await axios.post(`/api/items/${id}/quantity/${quantityNeeded}`)
    return resp.data
  },
  async setItemInCartForId ({ id, isInCart }) {
    const resp = await axios.post(`/api/items/${id}/cart/${isInCart ? 'in' : 'out'}`)
    return resp.data
  },
  async purchaseItemsInCart () {
    const resp = await axios.post(`/api/items/actions/purchase`)
    return resp.data
  },
  // endregion
  // region - Categories
  async getAllCategories () {
    const resp = await axios.get(`/api/categories`)
    return _.map(resp.data, internals.processCategory)
  },
  async getCategoryForId ({ id }) {
    const resp = await axios.get(`/api/categories/${id}`)
    return internals.processCategory(resp.data)
  },
  async saveCategory (category) {
    const resp = await (category.id
      ? axios.post(`/api/categories/${category.id}`, category)
      : axios.put(`/api/categories`, category))
    return internals.processCategory(resp.data)
  },
  async deleteCategoryForId ({ id }) {
    const resp = await axios.delete(`/categories/${id}`)
    return resp.data
  },
  async restoreCategoryForId ({ id }) {
    const resp = await axios.post(`/api/categories/${id}/restore`)
    return resp.data
  },
  // endregion
  // region - Meals
  async getAllMeals () {
    const resp = await axios.get(`/api/meals`)
    return _.map(resp.data, internals.processCategory)
  },
  async getMealForId ({ id }) {
    const resp = await axios.get(`/api/meals/${id}`)
    return internals.processMeal(resp.data)
  },
  async saveMeal (meal) {
    const resp = await (meal.id
      ? axios.post(`/api/meals/${meal.id}`, meal)
      : axios.put(`/api/meals`, meal))
    return internals.processMeal(resp.data)
  },
  async deleteMealForId ({ id }) {
    const resp = await axios.delete(`/meals/${id}`)
    return resp.data
  },
  async restoreMealForId ({ id }) {
    const resp = await axios.post(`/api/meals/${id}/restore`)
    return resp.data
  },
  // endregion
  // region - Stores
  async getAllStores () {
    const resp = await axios.get(`/api/stores`)
    return _.map(resp.data, internals.processCategory)
  },
  async getStoreForId ({ id }) {
    const resp = await axios.get(`/api/stores/${id}`)
    return internals.processStore(resp.data)
  },
  async saveStore (store) {
    const resp = await (store.id
      ? axios.post(`/api/stores/${store.id}`, store)
      : axios.put(`/api/stores`, store))
    return internals.processStore(resp.data)
  },
  async deleteStoreForId ({ id }) {
    const resp = await axios.delete(`/stores/${id}`)
    return resp.data
  },
  async restoreStoreForId ({ id }) {
    const resp = await axios.post(`/api/stores/${id}/restore`)
    return resp.data
  }
  // endregion
}

const publicApi = {
  // Auth:
  hasValidAuthKey: internals.hasValidAuthKey,
  setAuthKey: internals.setAuthKey,
  clearAuthKey: internals.clearAuthKey,
  // Items:
  getAllItems: internals.getAllItems,
  getNeededItems: internals.getNeededItems,
  getItemForId: internals.getItemForId,
  saveItem: internals.saveItem,
  deleteItemForId: internals.deleteItemForId,
  restoreItemForId: internals.restoreItemForId,
  setItemQuantityForId: internals.setItemQuantityForId,
  setItemInCartForId: internals.setItemInCartForId,
  purchaseItemsInCart: internals.purchaseItemsInCart,
  // Categories:
  getAllCategories: internals.getAllCategories,
  getCategoryForId: internals.getCategoryForId,
  saveCategory: internals.saveCategory,
  deleteCategoryForId: internals.deleteCategoryForId,
  restoreCategoryForId: internals.restoreCategoryForId,
  // Meals:
  getAllMeals: internals.getAllMeals,
  getMealForId: internals.getMealForId,
  saveMeal: internals.saveMeal,
  deleteMealForId: internals.deleteMealForId,
  restoreMealForId: internals.restoreMealForId,
  // Stores:
  getAllStores: internals.getAllStores,
  getStoreForId: internals.getStoreForId,
  saveStore: internals.saveStore,
  deleteStoreForId: internals.deleteStoreForId,
  restoreStoreForId: internals.restoreStoreForId
}

export default {
  install (Vue, { baseApiUrl }) {
    axios.defaults.baseURL = baseApiUrl

    // TODO: Error interceptors!

    Vue.prototype.$api = publicApi
    Vue.mixin({
      watch: {
        isSignedIn () {
          if (!this.isSignedIn) {
            this.$router.push('/login')
          }
        }
      },
      methods: {
        verifySignedIn () {
          if (!this.isSignedIn) {
            this.$router.push('/login')
          }
        }
      }
    })
  }
}
